import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { UserAvatar } from "@/components/common/UserAvatar";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { OffboardDialog } from "@/components/admin/OffboardDialog";
import { InlineSpinner } from "@/components/common/Loading";
import { PersonSearchSelect } from "@/components/common/PersonSearchSelect";
import { DeleteUserDialog } from "@/components/admin/DeleteUserDialog";
import { UserPlus, MoreHorizontal, Plus, Trash2, Users as UsersIcon, Send, Ban, Copy, Clock, Mail, ShieldCheck, Star, CheckCircle2, KeyRound } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const ROLES = [
  ["account_admin", "Account Admin"], ["group_admin", "Group Admin"], ["recruiting_manager", "Recruiting Manager"],
  ["internal_recruiter", "Internal Recruiter"], ["external_recruiter", "Outside Recruiter"],
  ["recruiting_support", "Support Staff"], ["agent", "Agent"], ["agent_staff", "Agent Staff"], ["referral_partner", "Referral Partner"],
  ["candidate", "Candidate"],
];

function fmtLastActive(r) {
  if (r.status === "invited") return "Invited — not accepted";
  const t = r.last_login_at;
  if (!t) return "Never Signed In";
  const d = new Date(t), now = new Date();
  if (d.toDateString() === now.toDateString()) return `Today, ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function Users() {
  const { currentAccount, user, ctx, activeAccount, isAdmin, isGroupAdmin, isSuper, isManager } = useAuth();
  const canManage = isAdmin || isGroupAdmin;
  const canApprove = isSuper || isAdmin || isManager || isGroupAdmin;
  const [users, setUsers] = useState([]);
  const [approving, setApproving] = useState(null);
  const [approveBusy, setApproveBusy] = useState(false);
  const [invites, setInvites] = useState([]);
  const [inviteSummary, setInviteSummary] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(false);
  const [editing, setEditing] = useState(null);
  const [linking, setLinking] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [offboarding, setOffboarding] = useState(null);

  // Group Admins may only assign roles below Account Admin, and only within their own org(s).
  const roleOptions = isAdmin ? ROLES : ROLES.filter(([k]) => k !== "account_admin");
  const groupOrgIds = isAdmin ? null : new Set(
    (ctx?.memberships || []).filter((m) => m.client_account_id === activeAccount && m.role === "group_admin").map((m) => m.organization_id)
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api.get("/users"), api.get("/organizations")];
      if (canManage) reqs.push(api.get("/users/invitations"));
      const [u, o, inv] = await Promise.all(reqs);
      setUsers(u.data.users); setOrgs(o.data.organizations);
      if (canManage && inv) { setInvites(inv.data.invitations || []); setInviteSummary(inv.data.summary || null); }
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [currentAccount, canManage]);
  useEffect(() => { load(); }, [load]);

  const resendInvite = async (inv) => {
    try { await api.post(`/users/invitations/${inv.id}/resend`, {}); toast.success("Invitation resent"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const revokeInvite = async (inv) => {
    try { await api.post(`/users/invitations/${inv.id}/revoke`); toast.success("Invitation revoked"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const copyLink = async (inv) => {
    if (!inv.accept_path) return toast.error("Link is no longer available for this invitation");
    try { await navigator.clipboard.writeText(`${window.location.origin}${inv.accept_path}`); toast.success("Invite link copied"); }
    catch { toast.error("Could not copy link"); }
  };

  const toggleStatus = async (u) => {
    try {
      if (u.status === "disabled") await api.post(`/users/${u.id}/enable`);
      else await api.post(`/users/${u.id}/disable`);
      toast.success("Updated"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const decide = async (approved) => {
    if (!approving) return;
    setApproveBusy(true);
    try {
      await api.post(`/interviews/outside-recruiters/${approving.id}`, { approved });
      toast.success(approved ? "Outside Recruiter approved" : "Outside Recruiter declined");
      setApproving(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setApproveBusy(false); }
  };

  const columns = [
    { key: "name", header: "User", render: (r) => (
      <div className="flex items-center gap-3">
        <UserAvatar name={r.name} size={32} />
        <div><p className="font-medium text-slate-800">{r.name}</p><p className="text-xs text-slate-400">{r.email}</p></div>
      </div>) },
    { key: "roles", header: "Districts & Access", render: (r) => (
      <div className="flex flex-wrap gap-1">
        {r.memberships?.map((m, i) => (
          <span key={i} className="text-xs rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-600">
            {m.role_label}{m.organization_name ? ` · ${m.organization_name}` : ""}{m.is_primary ? " ★" : ""}
          </span>
        ))}
      </div>) },
    { key: "linked", header: "Linked Person", render: (r) => (
      r.linked_person
        ? <span className="text-sm text-slate-700" data-testid={`user-linked-${r.id}`}>{r.linked_person.name}
            {r.linked_person.human_id && <span className="text-xs text-slate-400 font-mono"> · {r.linked_person.human_id}</span>}</span>
        : <span className="text-xs text-slate-400" data-testid={`user-linked-${r.id}`}>—</span>) },
    { key: "last_active", header: "Last Active", render: (r) => (
      <span className="text-xs text-slate-600 whitespace-nowrap" data-testid={`user-lastactive-${r.id}`}>{fmtLastActive(r)}</span>) },
    { key: "status", header: "Status", render: (r) => (
      <div className="flex flex-col gap-1">
        <StatusBadge status={r.status} />
        {r.approval_status && (r.approval_status === "pending" && canApprove
          ? <button data-testid={`user-approval-${r.id}`} onClick={() => setApproving(r)}
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 hover:text-amber-700 underline underline-offset-2">
              <Clock className="w-3 h-3" />Pending — Review &amp; Approve</button>
          : <span data-testid={`user-approval-${r.id}`}
              className={`text-[10px] font-semibold ${r.approval_status === "approved" ? "text-emerald-600" : "text-amber-600"}`}>
              {r.approval_status === "approved" ? "Approved" : "Pending Approval"}</span>)}
      </div>) },
    ...((canManage || canApprove) ? [{ key: "__actions", header: "", className: "text-right", render: (r) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild><button data-testid={`user-actions-${r.id}`} className="p-1 rounded hover:bg-slate-100"><MoreHorizontal className="w-4 h-4 text-slate-500" /></button></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white">
          {canApprove && r.approval_status && (
            <DropdownMenuItem onClick={() => setApproving(r)} data-testid={`user-approve-${r.id}`} className="text-emerald-700 font-medium">
              <ShieldCheck className="w-4 h-4 mr-2" />{r.approval_status === "approved" ? "Manage Outside Recruiter access" : "Review & Approve"}
            </DropdownMenuItem>)}
          {canManage && <DropdownMenuItem onClick={() => setEditing(r)} data-testid={`user-edit-${r.id}`}>Edit districts & role</DropdownMenuItem>}
          {canManage && <DropdownMenuItem onClick={() => setLinking(r)} data-testid={`user-link-${r.id}`}>Edit linked person</DropdownMenuItem>}
          {canManage && r.id !== user.id && (
            <DropdownMenuItem onClick={() => setResetting(r)} data-testid={`user-reset-password-${r.id}`}>
              <KeyRound className="w-4 h-4 mr-2" />Reset password
            </DropdownMenuItem>)}
          {canManage && r.id !== user.id && (
            <DropdownMenuItem onClick={() => toggleStatus(r)} className={r.status === "disabled" ? "text-emerald-600" : "text-rose-600"} data-testid={`user-toggle-${r.id}`}>
              {r.status === "disabled" ? "Reactivate User" : "Deactivate User / Remove Access"}
            </DropdownMenuItem>
          )}
          {canManage && r.id !== user.id && r.status !== "disabled" && (
            <DropdownMenuItem onClick={() => setOffboarding(r)} className="text-rose-700 font-medium" data-testid={`user-offboard-${r.id}`}>Offboard User…</DropdownMenuItem>
          )}
          {(isSuper || canManage) && r.id !== user.id && (
            <DropdownMenuItem onClick={() => setDeleting(r)} className="text-rose-700 font-medium" data-testid={`user-delete-${r.id}`}>
              <Trash2 className="w-4 h-4 mr-2" />Delete User
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>) }] : []),
  ];

  return (
    <div>
      <PageHeader title="Users" description={isSuper ? "Invite users and assign roles per organization." : canManage ? "Manage user roles per organization." : "View the user directory for your recruiting organization."}
        actions={isSuper ? <Button data-testid="invite-user-button" className="bg-[#006090] hover:bg-[#004E75]" onClick={() => setInvite(true)}><UserPlus className="w-4 h-4 mr-1.5" /> Invite User</Button> : null} />
      <DataTable columns={columns} rows={users} loading={loading} testId="users-table" resizeKey="users-list-columns"
        empty={<EmptyState icon={UsersIcon} title="No users yet" description={isSuper ? "Invite your first team member." : "No users to show."} />} />

      {canManage && <InvitationsSection invites={invites} summary={inviteSummary} onResend={isSuper ? resendInvite : null} onRevoke={revokeInvite} onCopy={copyLink} />}

      {invite && <MembershipDialog title="Invite User" orgs={groupOrgIds ? orgs.filter((o) => groupOrgIds.has(o.id)) : orgs} roleOptions={roleOptions} withIdentity onClose={() => setInvite(false)}
        onSave={async (payload) => { await api.post("/users/invite", payload); toast.success("Invitation sent"); setInvite(false); load(); }} />}
      {editing && <MembershipDialog title={`Edit ${editing.name}`} orgs={groupOrgIds ? orgs.filter((o) => groupOrgIds.has(o.id)) : orgs} roleOptions={roleOptions} initial={editing.memberships} onClose={() => setEditing(null)}
        onSave={async (payload) => { await api.put(`/users/${editing.id}/memberships`, { memberships: payload.memberships }); toast.success("Roles updated"); setEditing(null); load(); }} />}
      {linking && <LinkedPersonDialog userRow={linking} onClose={() => setLinking(null)} onSaved={() => { setLinking(null); load(); }} />}
      {resetting && <ResetPasswordDialog userRow={resetting} onClose={() => setResetting(null)} onSaved={() => setResetting(null)} />}
      {approving && <ApprovalDialog userRow={approving} accountName={currentAccount?.account_name || ""} busy={approveBusy} onClose={() => setApproving(null)} onDecide={decide} />}
      {offboarding && <OffboardDialog user={offboarding} users={users} onClose={() => setOffboarding(null)} onDone={(s) => { setOffboarding(null); toast.message(s); load(); }} />}
      {deleting && <DeleteUserDialog userRow={deleting} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); load(); }} />}
    </div>
  );
}

function ResetPasswordDialog({ userRow, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [temp, setTemp] = useState(null);
  const gen = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    const s = Array.from(crypto.getRandomValues(new Uint8Array(12))).map((b) => chars[b % chars.length]).join("");
    setPw(s);
  };
  const save = async () => {
    if (pw && pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${userRow.id}/reset-password`, pw ? { password: pw } : {});
      if (data.temporary_password) { setTemp(data.temporary_password); toast.success("Temporary password generated"); }
      else { toast.success(`Password reset for ${userRow.name}`); onSaved?.(); }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle>Reset password — {userRow.name}</DialogTitle></DialogHeader>
        {temp ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Share this one-time password securely. Ask {userRow.name} to change it after signing in.</p>
            <div className="flex items-center gap-2">
              <Input readOnly value={temp} className="font-mono" data-testid="reset-temp-password" />
              <Button variant="outline" onClick={() => { navigator.clipboard.writeText(temp); toast.success("Copied"); }} data-testid="reset-copy"><Copy className="w-4 h-4" /></Button>
            </div>
            <DialogFooter><Button onClick={() => onSaved?.()} className="bg-[#006090] hover:bg-[#004E75]" data-testid="reset-done">Done</Button></DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Set a new password for this user, or generate a temporary one they must change after signing in.</p>
            <div>
              <Label className="text-xs font-semibold text-slate-700">New password</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" data-testid="reset-password-input" />
                <Button type="button" variant="outline" onClick={gen} data-testid="reset-generate">Generate</Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} data-testid="reset-cancel">Cancel</Button>
              <Button onClick={save} disabled={busy} className="bg-[#006090] hover:bg-[#004E75]" data-testid="reset-save">{busy ? <InlineSpinner /> : "Reset password"}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ApprovalDialog({ userRow, accountName, busy, onClose, onDecide }) {
  const districts = (userRow.memberships || []).filter((m) => m.role === "external_recruiter");
  const approved = userRow.approval_status === "approved";
  const Field = ({ label, value }) => (
    <div className="flex justify-between gap-4 py-1 border-b border-slate-50 last:border-0">
      <span className="text-slate-400">{label}</span><span className="text-slate-800 font-medium text-right">{value}</span>
    </div>);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="approval-dialog">
        <DialogHeader><DialogTitle>Review Outside Recruiter</DialogTitle></DialogHeader>
        <div className="text-sm">
          <Field label="Name" value={userRow.name} />
          <Field label="Email" value={userRow.email} />
          <Field label="Role" value="Outside Recruiter" />
          <Field label="Client Account" value={accountName || "—"} />
          <div className="py-1 border-b border-slate-50">
            <span className="text-slate-400">Assigned District(s)</span>
            <div className="mt-1 flex flex-col items-end gap-0.5">
              {districts.length ? districts.map((d, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-slate-800 font-medium">
                  {d.is_primary && <Star className="w-3 h-3 text-amber-500 fill-amber-400" />}{d.organization_name}{d.is_primary ? " (Primary)" : ""}
                </span>)) : <span className="text-slate-500">—</span>}
            </div>
          </div>
          <Field label="Date invited" value={fmtDate(userRow.created_at)} />
          <Field label="Last Active" value={fmtLastActive(userRow)} />
          <Field label="Approval status" value={approved ? "Approved" : "Pending"} />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {approved
            ? <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => onDecide(false)} disabled={busy} data-testid="approval-decline">{busy ? <InlineSpinner /> : "Revoke access"}</Button>
            : <>
                <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => onDecide(false)} disabled={busy} data-testid="approval-decline">Decline</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onDecide(true)} disabled={busy} data-testid="approval-approve">{busy ? <InlineSpinner /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" />Approve</>}</Button>
              </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinkedPersonDialog({ userRow, onClose, onSaved }) {
  const [sel, setSel] = useState(userRow.linked_person?.id
    ? { id: userRow.linked_person.id, name: userRow.linked_person.name, human_id: userRow.linked_person.human_id } : null);
  const [loading, setLoading] = useState(false);
  const save = async (person_id) => {
    setLoading(true);
    try { await api.put(`/users/${userRow.id}/linked-person`, { person_id }); toast.success("Linked person updated"); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="linked-person-dialog">
        <DialogHeader><DialogTitle>Linked Person — {userRow.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">The CRM Person this login represents. Linking a Person does NOT grant any permissions — access is controlled only by Districts &amp; role.</p>
        <PersonSearchSelect selected={sel} onSelect={setSel} testId="link-person-search" />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => save(null)} disabled={loading} data-testid="linked-person-clear">Clear link</Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save(sel?.id)} disabled={loading || !sel} data-testid="linked-person-save" className="bg-[#006090] hover:bg-[#004E75]">{loading ? <InlineSpinner /> : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MembershipDialog({ title, orgs, roleOptions = ROLES, withIdentity, initial, onClose, onSave }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [expires, setExpires] = useState("7");
  const [rows, setRows] = useState(initial?.map((m) => ({ organization_id: m.organization_id, role: m.role, is_primary: m.is_primary }))
    || [{ organization_id: orgs[0]?.id || "", role: "internal_recruiter", is_primary: true }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  const primaryRow = rows.find((r) => r.is_primary) || rows[0];
  const doPreview = async () => {
    try {
      const { data } = await api.get("/users/invitation-preview", {
        params: { role: primaryRow?.role || "internal_recruiter", name: name || "", organization_id: primaryRow?.organization_id || "" },
      });
      setPreview(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const update = (i, k, v) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : (k === "is_primary" && v ? { ...r, is_primary: false } : r)));
  const addRow = () => setRows((rs) => [...rs, { organization_id: orgs[0]?.id || "", role: "internal_recruiter", is_primary: rs.length === 0 }]);
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (withIdentity && (!name || !email)) return setError("Name and email are required");
    if (rows.some((r) => !r.organization_id)) return setError("Select an organization for each membership");
    if (!rows.some((r) => r.is_primary)) rows[0].is_primary = true;
    setError(""); setLoading(true);
    try { await onSave({ name, email, memberships: rows, expires_days: Number(expires) }); }
    catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="membership-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto tr-scroll pr-1">
          {error && <p className="text-sm text-rose-600" data-testid="membership-error">{error}</p>}
          {withIdentity && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-slate-700">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="invite-name-input" /></div>
              <div><Label className="text-xs font-semibold text-slate-700">Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" data-testid="invite-email-input" /></div>
            </div>
          )}
          <Label className="text-xs font-semibold text-slate-700">Organization memberships</Label>
          {rows.map((r, i) => (
            <div key={i} className="rounded-md border border-slate-200 p-3 space-y-2" data-testid={`membership-row-${i}`}>
              <div className="grid grid-cols-2 gap-2">
                <Select value={r.organization_id} onValueChange={(v) => update(i, "organization_id", v)}>
                  <SelectTrigger data-testid={`membership-org-${i}`}><SelectValue placeholder="Organization" /></SelectTrigger>
                  <SelectContent className="bg-white">{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={r.role} onValueChange={(v) => update(i, "role", v)}>
                  <SelectTrigger data-testid={`membership-role-${i}`}><SelectValue placeholder="Role" /></SelectTrigger>
                  <SelectContent className="bg-white">{roleOptions.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <Checkbox checked={r.is_primary} onCheckedChange={(v) => update(i, "is_primary", !!v)} data-testid={`membership-primary-${i}`} /> Primary
                </label>
                {rows.length > 1 && <button onClick={() => removeRow(i)} className="text-rose-500" data-testid={`membership-remove-${i}`}><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addRow} data-testid="add-membership"><Plus className="w-4 h-4 mr-1" /> Add membership</Button>
          {withIdentity && (
            <div>
              <Label className="text-xs font-semibold text-slate-700">Invitation expires in</Label>
              <Select value={expires} onValueChange={setExpires}>
                <SelectTrigger className="mt-1" data-testid="invite-expiry-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {[["1", "1 day"], ["3", "3 days"], ["7", "7 days"], ["14", "14 days"], ["30", "30 days"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {withIdentity && <Button variant="outline" onClick={doPreview} data-testid="invite-preview-btn">Preview email</Button>}
          <Button onClick={submit} disabled={loading} data-testid="membership-submit" className="bg-[#006090] hover:bg-[#004E75]">{loading ? <InlineSpinner /> : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
      {preview && (
        <Dialog open onOpenChange={(o) => !o && setPreview(null)}>
          <DialogContent className="bg-white max-w-2xl" data-testid="invite-preview-dialog">
            <DialogHeader><DialogTitle>Email preview</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500" data-testid="invite-preview-subject"><strong>Subject:</strong> {preview.subject}</p>
            <iframe title="invite-preview" srcDoc={preview.html} className="w-full h-[60vh] border border-slate-200 rounded" data-testid="invite-preview-frame" />
            <DialogFooter><Button variant="outline" onClick={() => setPreview(null)}>Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}


const INV_BADGE = { pending: "in_review", expired: "inactive", accepted: "active", revoked: "inactive" };
const INV_LABEL = { pending: "Pending", expired: "Expired", accepted: "Accepted", revoked: "Revoked" };

function InvitationsSection({ invites, summary, onResend, onRevoke, onCopy }) {
  if (!invites || invites.length === 0) return null;
  const fmt = (d) => (d ? String(d).slice(0, 10) : "—");
  const chips = summary ? [
    ["Pending", summary.pending, "text-amber-700 bg-amber-50 border-amber-200"],
    ["Accepted", summary.accepted, "text-emerald-700 bg-emerald-50 border-emerald-200"],
    ["Expired", summary.expired, "text-slate-600 bg-slate-50 border-slate-200"],
    ["Revoked", summary.revoked, "text-rose-700 bg-rose-50 border-rose-200"],
    ["Awaiting response", summary.awaiting_response, "text-[#006090] bg-[#006090]/5 border-[#006090]/20"],
  ] : [];
  return (
    <div className="mt-10" data-testid="invitations-section">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="w-4 h-4 text-[#006090]" />
        <h2 className="font-display text-lg font-semibold text-slate-900">Invitations</h2>
        <span className="text-xs text-slate-400">({invites.length})</span>
      </div>
      {summary && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="invitations-summary">
          {chips.map(([label, val, cls]) => (
            <span key={label} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${cls}`} data-testid={`invite-stat-${label.toLowerCase().replace(/ /g, "-")}`}>
              {label}<strong className="tabular-nums">{val ?? 0}</strong>
            </span>
          ))}
        </div>
      )}
      <div className="overflow-x-auto tr-scroll rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="invitations-table">
          <thead>
            <tr className="text-left text-xs font-semibold text-slate-500 border-b border-slate-200 bg-slate-50">
              {["Invitee", "Roles & Scope", "Invited By", "Expires", "Status", ""].map((h) => <th key={h} className="px-3 py-2.5 whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {invites.map((inv) => {
              const active = inv.status === "pending" || inv.status === "expired";
              return (
                <tr key={inv.id} className="border-b border-slate-100 align-top" data-testid={`invite-row-${inv.id}`}>
                  <td className="px-3 py-3"><p className="font-medium text-slate-800">{inv.name}</p><p className="text-xs text-slate-400">{inv.email}</p></td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(inv.role_summary || []).map((r, i) => (
                        <span key={i} className="text-xs rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-600">{r.role_label}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-slate-600">{inv.invited_by_name || "—"}</td>
                  <td className="px-3 py-3 text-slate-600"><span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-slate-400" />{fmt(inv.expires_at)}</span></td>
                  <td className="px-3 py-3"><StatusBadge status={INV_BADGE[inv.status] || "in_review"} label={INV_LABEL[inv.status] || inv.status} /></td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {inv.accept_path && <Button size="sm" variant="outline" onClick={() => onCopy(inv)} data-testid={`invite-copy-${inv.id}`} className="h-8"><Copy className="w-3.5 h-3.5 mr-1" />Copy link</Button>}
                      {active && onResend && <Button size="sm" variant="outline" onClick={() => onResend(inv)} data-testid={`invite-resend-${inv.id}`} className="h-8"><Send className="w-3.5 h-3.5 mr-1" />Resend</Button>}
                      {inv.status !== "accepted" && inv.status !== "revoked" && <Button size="sm" variant="outline" onClick={() => onRevoke(inv)} data-testid={`invite-revoke-${inv.id}`} className="h-8 text-rose-600"><Ban className="w-3.5 h-3.5 mr-1" />Revoke</Button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
